import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate, formatMoney } from './format';
import { staleValue } from './StaleBlur';
import { InvoiceTypeBadge } from './InvoiceTypeBadge';
import { hasLimitedDetail } from './support';
import type { InvoiceRow, InvoiceSubject, KsefInvoice } from './types';

/** The counterparty depends on which side we hold the invoice from. */
const party = (inv: KsefInvoice, subject: InvoiceSubject) =>
  subject === 'SELLER'
    ? { name: inv.buyerName, id: inv.buyerIdentifier }
    : { name: inv.sellerName, id: inv.sellerNip };

function LimitedDetailBadge({ inv }: { inv: KsefInvoice }) {
  const { t } = useTranslation();
  if (!hasLimitedDetail(inv)) return null;
  return (
    <Badge
      variant="secondary"
      className="font-normal text-muted-foreground"
      title={t('invoices.limited_detail.hint')}
    >
      <Info data-icon="inline-start" />
      {t('invoices.limited_detail.label')}
    </Badge>
  );
}

function Party({ inv, subject }: { inv: KsefInvoice; subject: InvoiceSubject }) {
  const { name, id } = party(inv, subject);
  return (
    <div className="leading-tight">
      <div>{name ?? id ?? '—'}</div>
      {name && id ? <div className="text-xs text-muted-foreground">{id}</div> : null}
    </div>
  );
}

function money(lng: string, amount: number | null | undefined, currency?: string | null) {
  return amount != null ? formatMoney(lng, amount, currency ?? 'PLN') : '—';
}

/** Fraction of the loaded list scrolled past before the next page is prefetched. */
const PREFETCH_AT = 0.85;

/** The row that triggers the next-page fetch — ~85% down the currently-loaded list. */
const prefetchIndex = (count: number) => Math.floor(count * PREFETCH_AT);

interface RowProps<E extends HTMLElement> {
  inv: KsefInvoice;
  subject: InvoiceSubject;
  lng: string;
  onSelect: (inv: KsefInvoice) => void;
  /** Callback ref that arms the next-page prefetch; set only on the trigger row (see prefetchIndex). */
  triggerRef?: (node: E | null) => void;
}

// Memoized so appending a page only renders the new rows: with stable `inv`/`onSelect`/`lng`, every
// already-rendered row skips re-render (otherwise a long list re-formats hundreds of cells per page).
const InvoiceTableRow = memo(function InvoiceTableRow({
  inv,
  subject,
  lng,
  onSelect,
  triggerRef,
}: RowProps<HTMLTableRowElement>) {
  return (
    <TableRow ref={triggerRef} className="cursor-pointer" onClick={() => onSelect(inv)}>
      <TableCell className={cn('text-muted-foreground', staleValue)}>
        {formatDate(lng, inv.issueDate)}
      </TableCell>
      <TableCell className={cn('font-medium', staleValue)}>
        <div className="leading-tight">
          <div>{inv.invoiceNumber ?? '—'}</div>
          <div className="text-xs text-muted-foreground">{inv.ksefNumber}</div>
        </div>
      </TableCell>
      <TableCell className={staleValue}>
        <Party inv={inv} subject={subject} />
      </TableCell>
      <TableCell className={staleValue}>
        <div className="flex flex-wrap items-center gap-1">
          <InvoiceTypeBadge inv={inv} />
          <LimitedDetailBadge inv={inv} />
        </div>
      </TableCell>
      <TableCell className={cn('text-right tabular-nums text-muted-foreground', staleValue)}>
        {money(lng, inv.netAmount, inv.currency)}
      </TableCell>
      <TableCell className={cn('text-right tabular-nums text-muted-foreground', staleValue)}>
        {money(lng, inv.vatAmount, inv.currency)}
      </TableCell>
      <TableCell className={cn('text-right font-medium tabular-nums', staleValue)}>
        {money(lng, inv.grossAmount, inv.currency)}
      </TableCell>
    </TableRow>
  );
});

/**
 * Load-more wiring shared by the table and card list, driven by the infinite-scroll sentinel. All
 * optional so the components render fine without pagination (e.g. in isolation tests) — the sentinel
 * is simply not shown.
 */
interface LoadMore {
  /** More pages remain to fetch. */
  hasMore?: boolean;
  /** The next page is currently being fetched. */
  isLoadingMore?: boolean;
  /** Pull the next page (the sentinel calls this when it scrolls into view). */
  onLoadMore?: () => void;
}

export function InvoicesTable({
  rows,
  lng,
  onSelect,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore = () => {},
}: {
  rows: InvoiceRow[];
  lng: string;
  onSelect: (inv: KsefInvoice) => void;
} & LoadMore) {
  const { t } = useTranslation();
  const loadMoreRef = useInfiniteScroll<HTMLTableRowElement>(onLoadMore, hasMore && !isLoadingMore);
  const triggerIndex = prefetchIndex(rows.length);
  return (
    // Fill the height the list page hands down and scroll inside the ScrollArea; the header sticks
    // to the top of that scroll viewport so column labels stay visible.
    <Table containerClassName="min-h-0 flex-1">
      <TableHeader className="sticky top-0 z-10 bg-card">
        <TableRow>
          <TableHead>{t('invoices.columns.issued')}</TableHead>
          <TableHead>{t('invoices.columns.number')}</TableHead>
          <TableHead>{t('invoices.columns.party')}</TableHead>
          <TableHead>{t('invoices.columns.type')}</TableHead>
          <TableHead className="text-right">{t('invoices.columns.net')}</TableHead>
          <TableHead className="text-right">{t('invoices.columns.vat')}</TableHead>
          <TableHead className="text-right">{t('invoices.columns.amount')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map(({ invoice: inv, subject }, i) => (
          <InvoiceTableRow
            key={`${subject}-${inv.ksefNumber}`}
            inv={inv}
            subject={subject}
            lng={lng}
            onSelect={onSelect}
            triggerRef={i === triggerIndex ? loadMoreRef : undefined}
          />
        ))}
        {isLoadingMore ? (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={7} className="py-4 text-center text-sm text-muted-foreground">
              {t('invoices.loading_more')}
            </TableCell>
          </TableRow>
        ) : null}
      </TableBody>
    </Table>
  );
}

// Memoized for the same reason as InvoiceTableRow — only newly appended cards render on a page load.
const InvoiceCard = memo(function InvoiceCard({
  inv,
  subject,
  lng,
  onSelect,
  triggerRef,
}: RowProps<HTMLLIElement>) {
  const { t } = useTranslation();
  return (
    <li ref={triggerRef}>
      <Card
        role="button"
        tabIndex={0}
        onClick={() => onSelect(inv)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(inv);
          }
        }}
        className="cursor-pointer transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        <CardContent className={cn('flex items-start justify-between gap-3', staleValue)}>
          <div className="flex min-w-0 flex-col gap-1 leading-tight">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{inv.invoiceNumber ?? inv.ksefNumber}</span>
              <InvoiceTypeBadge inv={inv} />
              <LimitedDetailBadge inv={inv} />
            </div>
            <span className="text-xs text-muted-foreground">{formatDate(lng, inv.issueDate)}</span>
            <span className="truncate">{party(inv, subject).name ?? party(inv, subject).id ?? '—'}</span>
          </div>
          <div className="shrink-0 text-right leading-tight">
            <div className="font-medium tabular-nums">{money(lng, inv.grossAmount, inv.currency)}</div>
            <div className="text-xs text-muted-foreground tabular-nums">
              {t('invoices.columns.net')} {money(lng, inv.netAmount, inv.currency)}
            </div>
            <div className="text-xs text-muted-foreground tabular-nums">
              {t('invoices.columns.vat')} {money(lng, inv.vatAmount, inv.currency)}
            </div>
          </div>
        </CardContent>
      </Card>
    </li>
  );
});

/** Small-screen alternative to the table: one card per invoice. */
export function InvoicesCardList({
  rows,
  lng,
  onSelect,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore = () => {},
}: {
  rows: InvoiceRow[];
  lng: string;
  onSelect: (inv: KsefInvoice) => void;
} & LoadMore) {
  const { t } = useTranslation();
  const loadMoreRef = useInfiniteScroll<HTMLLIElement>(onLoadMore, hasMore && !isLoadingMore);
  const triggerIndex = prefetchIndex(rows.length);
  return (
    <ul className="space-y-3" aria-label={t('invoices.latest_in_month')}>
      {rows.map(({ invoice: inv, subject }, i) => (
        <InvoiceCard
          key={`${subject}-${inv.ksefNumber}`}
          inv={inv}
          subject={subject}
          lng={lng}
          onSelect={onSelect}
          triggerRef={i === triggerIndex ? loadMoreRef : undefined}
        />
      ))}
      {isLoadingMore ? (
        <li className="py-2 text-center text-sm text-muted-foreground">{t('invoices.loading_more')}</li>
      ) : null}
    </ul>
  );
}
